import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, History, Package, User, FileText, LogOut, Sparkles, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import StaffWorksheets from "./StaffWorksheets";
import StaffInventory from "./StaffInventory";
import StaffPayslips from "./StaffPayslips";

const TABS = [
  ["worksheets", "Worksheets", ClipboardList],
  ["history", "History", History],
  ["inventory", "Inventory", Package],
  ["payslip", "Payslip", FileText],
  ["profile", "Profile", User],
];

function Placeholder({ title, note }) {
  return (
    <div className="text-center py-16 px-6" data-testid="staff-placeholder">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-rose/10 text-rose mb-4"><Sparkles size={24} /></div>
      <h3 className="font-serif text-xl text-forest">{title}</h3>
      <p className="text-sm text-stone-500 mt-2 max-w-xs mx-auto">{note}</p>
    </div>
  );
}

function Profile() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/staff/profile").then((r) => setP(r.data)).catch(() => setP(false)); }, []);
  if (p === null) return <Loader2 className="animate-spin text-rose mx-auto my-10" />;
  if (!p) return <Placeholder title="Profile unavailable" note="We couldn't load your profile right now." />;
  const rows = [
    ["Name", p.name], ["Employee No.", p.employee_number], ["Payroll No.", p.payroll_number],
    ["Username", p.username], ["Job title", p.job_title], ["Category", p.staff_category],
    ["Mobile", p.mobile || "—"], ["Email", p.email || "—"], ["Status", p.active === false ? "Inactive" : "Active"],
  ];
  return (
    <div className="p-4" data-testid="staff-profile">
      <div className="card-lux p-5">
        <h3 className="font-serif text-xl text-forest mb-4">My profile</h3>
        <dl className="space-y-2.5">
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between text-sm border-b border-stone-100 pb-2 last:border-0">
              <dt className="text-stone-500">{k}</dt><dd className="font-medium text-forest text-right">{v}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

export default function StaffPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("worksheets");

  const signOut = async () => { await logout(); navigate("/staff/login"); };

  return (
    <div className="min-h-screen bg-sand/40 flex flex-col" data-testid="staff-portal">
      <header className="bg-forest text-cream px-4 py-3 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-2"><Sparkles size={16} className="text-gold" /><span className="font-serif text-lg">The Rose Airbnb</span></div>
        <button onClick={signOut} data-testid="staff-logout" className="text-cream/80 hover:text-white inline-flex items-center gap-1 text-sm"><LogOut size={16} /> Sign out</button>
      </header>

      <div className="px-4 py-3 bg-white border-b border-stone-100">
        <div className="text-sm text-stone-500">Signed in as</div>
        <div className="font-medium text-forest">{user?.name || user?.username} <span className="text-xs text-stone-400">· {user?.employee_number}</span></div>
      </div>

      <main className="flex-1 pb-20">
        {tab === "worksheets" && <StaffWorksheets />}
        {tab === "history" && <StaffWorksheets history />}
        {tab === "inventory" && <StaffInventory />}
        {tab === "payslip" && <StaffPayslips />}
        {tab === "profile" && <Profile />}
      </main>

      <nav className="fixed bottom-0 inset-x-0 bg-white border-t border-stone-200 grid grid-cols-5 z-20" data-testid="staff-bottom-nav">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`staff-tab-${id}`}
            className={`flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium ${tab === id ? "text-rose" : "text-stone-400"}`}>
            <Icon size={20} /> {label}
          </button>
        ))}
      </nav>
    </div>
  );
}
